import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuthStore } from '../store/auth';

export interface LookupOption {
  id: string;
  name: string;
}

interface LookupSource {
  endpoint: string;
  /** When true, only fetch for supervisor/admin roles (some endpoints 403 for agents). */
  supervisorOnly?: boolean;
}

const SOURCES: Record<string, LookupSource> = {
  agents: { endpoint: '/reports/calls/users' },
  campaigns: { endpoint: '/campaigns' },
  queues: { endpoint: '/reports/queue/list', supervisorOnly: true },
  ringGroups: { endpoint: '/reports/ring-groups/list', supervisorOnly: true },
  twilioNumbers: { endpoint: '/reports/queue/dids-list', supervisorOnly: true },
};

export type LookupKind = 'agents' | 'campaigns' | 'queues' | 'ringGroups' | 'twilioNumbers';

export function useLookup(kind: LookupKind): { options: LookupOption[]; isLoading: boolean; available: boolean } {
  const role = useAuthStore((s) => s.user?.role);
  const source = SOURCES[kind]!;
  const available = !source.supervisorOnly || role === 'admin' || role === 'supervisor';

  const query = useQuery({
    queryKey: ['lookup', kind],
    queryFn: async () => {
      const raw = await api.get<unknown>(source.endpoint);
      return normalizeLookup(raw);
    },
    enabled: available,
    staleTime: 5 * 60 * 1000,
  });

  return { options: query.data ?? [], isLoading: query.isLoading, available };
}

function normalizeLookup(raw: unknown): LookupOption[] {
  const list = Array.isArray(raw)
    ? raw
    : Array.isArray((raw as { data?: unknown })?.data)
      ? (raw as { data: unknown[] }).data
      : [];
  return list
    .map((item) => {
      const obj = item as { id?: unknown; name?: unknown };
      if (typeof obj.id !== 'string' || typeof obj.name !== 'string') return null;
      return { id: obj.id, name: obj.name };
    })
    .filter((v): v is LookupOption => v !== null);
}
